const MASS_IN_G: Record<string, number> = { g: 1, kg: 1000 };
const VOLUME_IN_ML: Record<string, number> = { ml: 1, l: 1000 };
const COUNT: Record<string, number> = { pcs: 1 };
const DISCRETE = new Set(["pcs", "bag", "bottle"]);
const MEASUREMENT = new Set(["g", "kg", "ml", "l"]);

export type UnitFamily = "mass" | "volume" | "count" | "pack";

export class UnitConversionError extends Error {
  readonly code: "UNIT_FAMILY_MISMATCH" | "INVALID_FACTOR";

  constructor(code: "UNIT_FAMILY_MISMATCH" | "INVALID_FACTOR") {
    super(code);
    this.name = "UnitConversionError";
    this.code = code;
  }
}

export type ResolvedIngredientUnits = {
  receiveUnit: string;
  receiveToBase: number;
  issueUnit: string;
  issueToBase: number;
};

export function unitFamily(unit: string): UnitFamily {
  const key = unit.trim().toLowerCase();
  if (key in MASS_IN_G) return "mass";
  if (key in VOLUME_IN_ML) return "volume";
  if (key in COUNT) return "count";
  return "pack";
}

/** 1 `unit` = กี่ `baseUnit`. คืน null เมื่อเป็นแพ็ก หรือคนละครอบครัว. */
export function standardFactor(baseUnit: string, unit: string): number | null {
  const base = baseUnit.trim().toLowerCase();
  const next = unit.trim().toLowerCase();
  if (DISCRETE.has(base) || DISCRETE.has(next)) {
    return base === next ? 1 : null;
  }
  const table =
    base in MASS_IN_G
      ? MASS_IN_G
      : base in VOLUME_IN_ML
        ? VOLUME_IN_ML
        : base in COUNT
          ? COUNT
          : null;
  if (!table) return null;
  const baseSize = table[base];
  const unitSize = table[next];
  if (baseSize == null || unitSize == null) return null;
  return unitSize / baseSize;
}

export function toBaseQty(qty: number, factor: number): number {
  return qty * factor;
}

/** หน่วยฐานหยาบ (pcs, bag, bottle) คู่กับหน่วยขายที่วัดละเอียด (g, ml). อัตราคือปริมาณละเอียดใน 1 หน่วยฐาน. */
export function isContentFactor(baseUnit: string, unit: string): boolean {
  const base = baseUnit.trim().toLowerCase();
  const next = unit.trim().toLowerCase();
  return DISCRETE.has(base) && MEASUREMENT.has(next);
}

/** จำนวนบนเอกสารสูตร/ขาย → หน่วยฐาน. หน่วยขายละเอียดต่อหน่วยฐานหยาบใช้การหาร. */
export function qtyToBase(
  qty: number,
  factor: number,
  baseUnit: string,
  unit: string,
): number {
  if (!(factor > 0) || !Number.isFinite(factor)) {
    throw new UnitConversionError("INVALID_FACTOR");
  }
  if (isContentFactor(baseUnit, unit)) return qty / factor;
  return qty * factor;
}

export function fromBaseQty(qtyBase: number, factor: number): number {
  if (!(factor > 0)) throw new UnitConversionError("INVALID_FACTOR");
  return qtyBase / factor;
}

/** ต้นทุนต่อหน่วยฐาน. ปัดเป็นจำนวนเต็มกีบ และไม่ให้เหลือ 0 เมื่อต้นทุนบรรทัดยังมากกว่า 0. */
export function costPerBaseLak(unitCostLak: number, factor: number): number {
  if (!(factor > 0) || !Number.isFinite(factor)) {
    throw new UnitConversionError("INVALID_FACTOR");
  }
  const rounded = Math.round(unitCostLak / factor);
  if (rounded === 0 && unitCostLak > 0) return 1;
  return rounded;
}

function canonicalUnit(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length < 1 || trimmed.length > 16) {
    throw new UnitConversionError("INVALID_FACTOR");
  }
  const lower = trimmed.toLowerCase();
  if (unitFamily(lower) !== "pack" || DISCRETE.has(lower)) return lower;
  return trimmed;
}

function resolveSide(
  baseUnit: string,
  unit: string | null | undefined,
  factor: number | null | undefined,
  options?: { content?: boolean },
): { unit: string; factor: number } {
  const base = baseUnit.trim().toLowerCase();
  if (unit == null || unit.trim() === "") {
    return { unit: base, factor: 1 };
  }
  const label = canonicalUnit(unit);
  if (label === base) return { unit: base, factor: 1 };
  if (options?.content && isContentFactor(base, label)) {
    if (factor == null || !Number.isFinite(factor) || factor <= 0) {
      throw new UnitConversionError("INVALID_FACTOR");
    }
    return { unit: label, factor };
  }
  if (unitFamily(label) !== "pack") {
    const fixed = standardFactor(base, label);
    if (fixed == null) throw new UnitConversionError("UNIT_FAMILY_MISMATCH");
    return { unit: label, factor: fixed };
  }
  if (factor == null || !Number.isFinite(factor) || factor <= 0) {
    throw new UnitConversionError("INVALID_FACTOR");
  }
  return { unit: label, factor };
}

export function resolveIngredientUnits(input: {
  baseUnit: string;
  receiveUnit?: string | null;
  receiveToBase?: number | null;
  issueUnit?: string | null;
  issueToBase?: number | null;
}): ResolvedIngredientUnits {
  const receive = resolveSide(
    input.baseUnit,
    input.receiveUnit,
    input.receiveToBase,
  );
  const issue = resolveSide(
    input.baseUnit,
    input.issueUnit,
    input.issueToBase,
    { content: true },
  );
  return {
    receiveUnit: receive.unit,
    receiveToBase: receive.factor,
    issueUnit: issue.unit,
    issueToBase: issue.factor,
  };
}
